import { SelectionModel } from '@angular/cdk/collections';
import { AsyncPipe, JsonPipe, NgClass, NgFor, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { Component, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CanDisable, mixinDisabled } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import {
  MatLegacyTableDataSource as MatTableDataSource,
  MatLegacyTableModule as MatTableModule,
} from '@angular/material/legacy-table';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import {
  ButtReplacementCustomDialogComponent,
  ButtReplacementCustomDialogData,
} from '@cutting/main/butt-replacement-custom-dialog/butt-replacement-custom-dialog.component';
import { LocalStorageManager } from '@cutting/main/shared/helper/local-storage-manager.helper';
import {
  AvailableFacadeMaterial,
  BazisMaterialHelper,
  CuttingButt,
  CuttingMaterial,
  CuttingMaterialBaseDialogComponent,
  CuttingMaterialBaseDialogData,
  CuttingPanelListService,
  CuttingSettings,
  FacadeTextureDirectionEnum,
  FacadeView,
  Guid,
  LegacyDialogsService,
  NgxLocalizedNumbers,
  NgxLocalizedNumbersService,
} from 'cloud-shared-lib';
import { cloneDeep, groupBy, orderBy, toPairs } from 'lodash';
import { Observable, Subject, concatMap, finalize, of, tap } from 'rxjs';

import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { FacadesAvailableComponentsService } from 'projects/cloud-shared-lib/src/lib/modules/facade-settings/facades-available-components.service';
import { GroupedFacadesView } from '../panel-list.component';
class GroupsFacade {
  totalCounts = 0;
  title: string;
  area: number;
  facade: FacadeView[];
  material: GroupedFacadesView;
  filmMaterial: CuttingMaterial;
  buttMaterial: CuttingButt;
}

export class PanelListTableBase {}

// eslint-disable-next-line no-underscore-dangle
export const _PanelListTableMixinBase = mixinDisabled(PanelListTableBase);

@Component({
  selector: 'cutting-panel-list-table',
  templateUrl: './panel-list-table.component.html',
  styleUrls: ['./panel-list-table.component.scss'],
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  inputs: ['disabled'],
  standalone: true,
  imports: [
    JsonPipe,
    NgIf,
    NgFor,
    NgClass,
    NgSwitch,
    NgSwitchCase,
    NgSwitchDefault,
    AsyncPipe,
    FormsModule,
    NgxLocalizedNumbers,
    MatIconModule,
    MatSortModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatMenuModule,
  ],
})
export class PanelListTableComponent extends _PanelListTableMixinBase implements OnChanges, CanDisable {
  @Input() editMode = false;
  @Input({ required: true }) materials: GroupedFacadesView[];
  @Input() editablePanelUuid = '';
  @Input({ required: true }) cuttingSettings: CuttingSettings;

  @Output() editing: Subject<FacadeView> = new Subject();
  @Output() copying: Subject<FacadeView> = new Subject();
  @Output() deleting: Subject<FacadeView> = new Subject();
  @Output() changeEdges: Subject<FacadeView[]> = new Subject();
  @Output() changeMaterial: Subject<{ facades: FacadeView[]; newMaterial: CuttingMaterial }> = new Subject();

  @ViewChild(MatSort) sort: MatSort;

  columns = [
    'combined',
    'height',
    'width',
    'count',
    'direction',
    'edge',
    'fasteners',
    'angles-processing',
    'slots',
    'handles',
    'remark',
    'menu',
  ];

  columnsHeader = [
    'height',
    'width',
    'count',
    'direction',
    'edge',
    'fasteners',
    'angles-processing',
    'slots',
    'handles',
    'remark',
    'menu',
  ];

  innerDisplayedColumns = [
    'height',
    'width',
    'count',
    'direction',
    'edge',
    'fasteners',
    'angles-processing',
    'slots',
    'handles',
    'remark',
    'menu',
  ];
  public dataSource = new MatTableDataSource<GroupsFacade>([]);

  expandedSubFacade: FacadeView[] = [];

  selectionMode = false;
  bazisMaterialHelper = BazisMaterialHelper;
  selection = new SelectionModel<FacadeView>(true);
  activeMaterial: { material: GroupedFacadesView; filmMaterial: CuttingMaterial; buttMaterial: CuttingButt };
  replacer: () => void;
  action$: Observable<any>;

  get isShowMaterialsPrice(): boolean {
    return this.cuttingSettings.estimateSettings.showMaterialsPrice;
  }

  get userId(): number {
    return LocalStorageManager.regData;
  }

  constructor(
    private dialogs: LegacyDialogsService,
    private cuttingPanelListService: CuttingPanelListService,
    private localizedNumbersService: NgxLocalizedNumbersService,
    private facadesAvailableComponentsService: FacadesAvailableComponentsService
  ) {
    super();
    this.activeMaterial = {
      material: null,
      filmMaterial: null,
      buttMaterial: null,
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.materials?.currentValue) {
      const val = changes.materials.currentValue as GroupedFacadesView[];

      this.setData(val);
    }

    if (changes?.selectionMode?.currentValue) {
      this.selection.clear();
    }
  }

  setData(val: GroupedFacadesView[]): void {
    const groupsFacades: GroupsFacade[] = val.flatMap(m => {
      const groupedFacades = groupBy(m.facades, facade => {
        const materials = this.getGroupingMaterial(facade);
        return materials.buttMaterial?.name || materials.filmMaterial?.name;
      });

      return toPairs(groupedFacades).map(([groupKey, facades]) => {
        const firstFacade = facades[0];
        const groupingMaterial = this.getGroupingMaterial(firstFacade);
        return this.createGroupsFacade(m, facades, groupingMaterial);
      });
    });

    this.dataSource.data = groupsFacades;
  }

  private createGroupsFacade(
    m: GroupedFacadesView,
    facades: FacadeView[],
    groupingMaterial: { filmMaterial: CuttingMaterial | null; buttMaterial: CuttingButt | null }
  ): GroupsFacade {
    return {
      title: this.getMaterialName(m),
      totalCounts: facades.length,
      area: this.calculateArea(facades),
      facade: facades,
      material: m,
      filmMaterial: groupingMaterial.filmMaterial,
      buttMaterial: groupingMaterial.buttMaterial,
    } as GroupsFacade;
  }

  onSortData(sort: Sort): void {
    const sorted = cloneDeep(this.materials);

    if (sort.active && sort.direction !== '') {
      sorted.forEach(m => {
        m.facades = orderBy(m.facades, [sort.active], [sort.direction as 'asc' | 'desc']);
      });
    }

    this.setData(sorted);
  }

  getDirectionIcon(dir: FacadeTextureDirectionEnum): string {
    switch (dir) {
      case FacadeTextureDirectionEnum.AlongHeight:
        return 'arrow_forward';

      case FacadeTextureDirectionEnum.AlongWidth:
        return 'arrow_upward';

      default:
        return '';
    }
  }

  hasFasteners(panel: FacadeView): boolean {
    return (
      !!panel?.panelOutline?.topSide?.fastenersSets?.length ||
      !!panel?.panelOutline?.rightSide?.fastenersSets?.length ||
      !!panel?.panelOutline?.bottomSide?.fastenersSets?.length ||
      !!panel?.panelOutline?.leftSide?.fastenersSets?.length ||
      !!panel?.panelOutline?.topSide?.customHoles?.length ||
      !!panel?.panelOutline?.rightSide?.customHoles?.length ||
      !!panel?.panelOutline?.bottomSide?.customHoles?.length ||
      !!panel?.panelOutline?.leftSide?.customHoles?.length
    );
  }

  hasMilling(panel: FacadeView): boolean {
    return !!panel?.panelOutline?.millings?.length;
  }

  hasEdges(panel: FacadeView): boolean {
    return !!panel?.l1 || !!panel?.l2 || !!panel?.w1 || !!panel?.w2;
  }

  hasAngleProcessing(panel: FacadeView): boolean {
    return (
      !!panel?.panelOutline?.topLeft?.mode ||
      !!panel?.panelOutline?.topRight?.mode ||
      !!panel?.panelOutline?.bottomRight?.mode ||
      !!panel?.panelOutline?.bottomLeft?.mode
    );
  }

  hasSlots(panel: FacadeView): boolean {
    return (
      !!panel?.panelOutline?.topSide?.groove ||
      !!panel?.panelOutline?.rightSide?.groove ||
      !!panel?.panelOutline?.bottomSide?.groove ||
      !!panel?.panelOutline?.leftSide?.groove
    );
  }

  hasHandle(panel: FacadeView): boolean {
    return (
      !!panel?.panelOutline?.topSide?.handle ||
      !!panel?.panelOutline?.rightSide?.handle ||
      !!panel?.panelOutline?.bottomSide?.handle ||
      !!panel?.panelOutline?.leftSide?.handle
    );
  }

  getMaterialName(material: GroupedFacadesView): string {
    return material ? BazisMaterialHelper.formatMaterialNameAndArticle(material.name, material.article) : '';
  }

  delete(panel: FacadeView): void {
    this.deleting.next(panel);
  }

  edit(item: FacadeView): void {
    this.editing.next(item);
  }

  copy(item: FacadeView): void {
    const newPanel = cloneDeep(item);
    newPanel.uuid = Guid.newGuid();

    this.copying.next(newPanel);

    this.edit(newPanel);
  }

  isEditing(item: FacadeView): boolean {
    if (this.editablePanelUuid) {
      return this.editMode && this.editablePanelUuid === item.uuid;
    }
  }

  startReplacing(replacer: () => void): void {
    this.selectionMode = true;
    this.replacer = replacer;

    if (!this.columns.includes('selected')) {
      this.columns.unshift('selected');
    }

    if (!this.innerDisplayedColumns.includes('selected')) {
      this.innerDisplayedColumns.unshift('selected');
    }

    if (!this.columnsHeader.includes('selected')) {
      this.columnsHeader.unshift('selected');
    }
  }

  replace(): void {
    this.replacer();
  }

  replaceMaterial(material: GroupedFacadesView, filmMaterial: CuttingMaterial, buttMaterial: CuttingButt): () => void {
    this.activeMaterial.material = material;
    this.activeMaterial.filmMaterial = filmMaterial;
    this.activeMaterial.buttMaterial = buttMaterial;

    return (): void => {
      this.action$ = this.dialogs
        .component<CuttingMaterialBaseDialogData, AvailableFacadeMaterial[]>(
          CuttingMaterialBaseDialogComponent,
          {
            showPrices: this.isShowMaterialsPrice,
            currencyName: this.getCurrencySymbol(),
            materialsToExclude: [],
            dataProvider: this.facadesAvailableComponentsService,
          } as CuttingMaterialBaseDialogData,
          1150
        )
        .pipe(
          concatMap(result => {
            if (result && result.length > 0) {
              this.changeMaterial.next({
                facades: this.selection.selected,
                newMaterial: result[0],
              });
            }
            if (result[0].film) {
              this.activeMaterial.buttMaterial = null;
            } else {
              const hasEdges = this.selection.selected.reduce((acc, val) => acc || this.hasEdges(val), false);

              if (hasEdges) {
                return this.getEdgeReplacer(result[0].inMaterialBaseId, result[0].thickness);
              }
              this.activeMaterial.filmMaterial = null;
            }

            return of({});
          }),
          finalize(() => this.stopReplacing())
        );
    };
  }

  replaceEdge(material: GroupedFacadesView, filmMaterial: CuttingMaterial, buttMaterial: CuttingButt): () => void {
    this.activeMaterial.material = material;
    this.activeMaterial.filmMaterial = filmMaterial;
    this.activeMaterial.buttMaterial = buttMaterial;

    return (): void => {
      this.action$ = this.getEdgeReplacer(material.inMaterialBaseId, material.thickness).pipe(
        finalize(() => this.stopReplacing())
      );
    };
  }

  getEdgeReplacer(inMaterialBaseId: number, thickness: number): Observable<FacadeView[]> {
    return this.dialogs
      .component<ButtReplacementCustomDialogData, FacadeView[]>(
        ButtReplacementCustomDialogComponent,
        {
          panels: this.selection.selected,
          matInMatBaseId: inMaterialBaseId,
          materialThickness: thickness,
          onlyInColor: true,
          useAllIfInColorEmpty: true,
        } as ButtReplacementCustomDialogData,
        500
      )
      .pipe(
        tap(result => {
          if (result) {
            this.changeEdges.next(result);
          }
        })
      );
  }

  stopReplacing(): void {
    this.selectionMode = false;
    this.selection.clear();
    this.activeMaterial = {
      material: null,
      filmMaterial: null,
      buttMaterial: null,
    };

    this.columns = this.columns.filter(column => column !== 'selected');
    this.innerDisplayedColumns = this.innerDisplayedColumns.filter(column => column !== 'selected');
    this.columnsHeader = this.innerDisplayedColumns.filter(column => column !== 'selected');
  }

  getCurrencySymbol(): string {
    return this.localizedNumbersService.getCurrentLocaleDefinition().currency;
  }

  isActiveMaterialRow(row: FacadeView): boolean {
    return this.compareMaterials(
      this.activeMaterial?.material?.inMaterialBaseId,
      this.activeMaterial?.filmMaterial?.inMaterialBaseId,
      this.activeMaterial?.buttMaterial,
      row.material.inMaterialBaseId,
      row.panelOutline?.filmMaterial?.inMaterialBaseId,
      row?.l1 || row?.l2 || row?.w1 || row?.w2
    );
  }

  isActiveMaterial(material: GroupedFacadesView, filmMaterial: CuttingMaterial, buttMaterial: CuttingButt): boolean {
    if (!this.activeMaterial || !this.activeMaterial.material) {
      return false;
    }

    return this.compareMaterials(
      this.activeMaterial?.material?.inMaterialBaseId,
      this.activeMaterial?.filmMaterial?.inMaterialBaseId,
      this.activeMaterial?.buttMaterial,
      material?.inMaterialBaseId,
      filmMaterial?.inMaterialBaseId,
      buttMaterial
    );
  }

  isAllSelected(item: GroupsFacade): boolean {
    return this.selection.selected.some(res => item.facade.includes(res));
  }

  masterToggle(item: GroupsFacade): void {
    if (this.isAllSelected(item)) {
      this.selection.clear();
      return;
    }

    this.selection.select(...item.facade);
  }

  private compareMaterials(
    activeMaterialId: number,
    activeFilmMaterialId: number,
    activeButtMaterial: CuttingButt,
    materialId: number,
    filmMaterialId: number,
    buttMaterial: CuttingButt
  ): boolean {
    const isSameMaterial = activeMaterialId === materialId;
    const isSameFilmMaterial = activeFilmMaterialId === filmMaterialId;
    const isSameButtMaterial = this.compareButtMaterials(activeButtMaterial, buttMaterial);

    return isSameMaterial && isSameFilmMaterial && isSameButtMaterial;
  }

  private compareButtMaterials(materialButt1: CuttingButt, materialButt2: CuttingButt): boolean {
    if (!materialButt1 && !materialButt2) return true;
    if (!materialButt1 || !materialButt2) return false;

    return BazisMaterialHelper.compareMaterialNames(
      materialButt1.name,
      materialButt1.article,
      materialButt2.name,
      materialButt2.article
    );
  }

  private getGroupingMaterial(facade: FacadeView): {
    filmMaterial: CuttingMaterial | null;
    buttMaterial: CuttingButt | null;
  } {
    const buttMaterial = facade.l1 || facade.l2 || facade.w1 || facade.w2 || null;

    const filmMaterial = facade.panelOutline?.filmMaterial || null;

    return { filmMaterial, buttMaterial };
  }

  private calculateArea(facades: FacadeView[]): number {
    return facades.reduce((acc, facade) => acc + (facade.height * facade.width * facade.count) / 1e6, 0);
  }
}
